import CryptoJS from 'crypto-js';

/**
 * AES加密工具类
 */
export const AESUtil = {
  // 加密模式配置
  MODES: {
    ECB: {
      name: 'ECB',
      requiresIV: false,
      mode: CryptoJS.mode.ECB,
      description: '最简单的模式，不需要IV，但安全性较低'
    },
    CBC: {
      name: 'CBC',
      requiresIV: true,
      mode: CryptoJS.mode.CBC,
      description: '最常用的模式，需要IV，安全性好'
    },
    CFB: {
      name: 'CFB',
      requiresIV: true,
      mode: CryptoJS.mode.CFB,
      description: '将分组密码转换为流密码，需要IV'
    },
    OFB: {
      name: 'OFB',
      requiresIV: true,
      mode: CryptoJS.mode.OFB,
      description: '将分组密码转换为同步流密码，需要IV'
    },
    CTR: {
      name: 'CTR',
      requiresIV: true,
      mode: CryptoJS.mode.CTR,
      description: '计数器模式，支持并行处理，需要IV'
    }
  },

  /**
   * AES 加密
   * @param {string} plainText - 待加密的明文
   * @param {string} key - 密钥
   * @param {string} iv - 初始向量
   * @param {string} paddingMode - 填充模式: 'PKCS7', 'Zero', 'NoPadding'
   * @param {string} encryptMode - 加密模式: 'ECB', 'CBC', 'CFB', 'OFB', 'CTR'
   * @returns {string} - 加密后的密文（Base64编码）
   */
  encrypt: (plainText, key, iv, paddingMode, encryptMode = 'CBC') => {
    try {
      // 获取加密模式配置
      const modeConfig = AESUtil.MODES[encryptMode];
      if (!modeConfig) {
        throw new Error(`不支持的加密模式: ${encryptMode}`);
      }

      // 将密钥转换为CryptoJS格式
      const keyHex = CryptoJS.enc.Utf8.parse(key);
      
      // 配置加密选项
      const options = {
        mode: modeConfig.mode
      };
      
      // 如果模式需要IV，则添加IV
      if (modeConfig.requiresIV) {
        if (!iv) {
          throw new Error(`${encryptMode}模式需要提供初始向量(IV)`);
        }
        const ivHex = CryptoJS.enc.Utf8.parse(iv);
        options.iv = ivHex;
      }
      
      // 根据填充模式选择不同的填充方式
      switch (paddingMode) {
        case 'Zero':
          options.padding = CryptoJS.pad.ZeroPadding;
          break;
        case 'NoPadding':
          options.padding = CryptoJS.pad.NoPadding;
          break;
        case 'PKCS7':
        default:
          options.padding = CryptoJS.pad.Pkcs7;
      }
      
      // 执行加密
      const encrypted = CryptoJS.AES.encrypt(plainText, keyHex, options);
      
      // 返回Base64编码的密文
      return encrypted.toString();
    } catch (error) {
      throw new Error(`加密错误: ${error.message}`);
    }
  },
  
  /**
   * AES 解密
   * @param {string} cipherText - 待解密的密文（Base64编码）
   * @param {string} key - 密钥
   * @param {string} iv - 初始向量
   * @param {string} paddingMode - 填充模式: 'PKCS7', 'Zero', 'NoPadding'
   * @param {string} encryptMode - 加密模式: 'ECB', 'CBC', 'CFB', 'OFB', 'CTR'
   * @returns {string} - 解密后的明文
   */
  decrypt: (cipherText, key, iv, paddingMode, encryptMode = 'CBC') => {
    try {
      // 获取加密模式配置
      const modeConfig = AESUtil.MODES[encryptMode];
      if (!modeConfig) {
        throw new Error(`不支持的加密模式: ${encryptMode}`);
      }

      // 将密钥转换为CryptoJS格式
      const keyHex = CryptoJS.enc.Utf8.parse(key);
      
      // 配置解密选项
      const options = {
        mode: modeConfig.mode
      };
      
      // 如果模式需要IV，则添加IV
      if (modeConfig.requiresIV) {
        if (!iv) {
          throw new Error(`${encryptMode}模式需要提供初始向量(IV)`);
        }
        const ivHex = CryptoJS.enc.Utf8.parse(iv);
        options.iv = ivHex;
      }
      
      // 根据填充模式选择不同的填充方式
      switch (paddingMode) {
        case 'Zero':
          options.padding = CryptoJS.pad.ZeroPadding;
          break;
        case 'NoPadding':
          options.padding = CryptoJS.pad.NoPadding;
          break;
        case 'PKCS7':
        default:
          options.padding = CryptoJS.pad.Pkcs7;
      }
      
      // 执行解密
      const decrypted = CryptoJS.AES.decrypt(cipherText, keyHex, options);
      
      // 将解密结果转换为UTF-8字符串
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      throw new Error(`解密错误: ${error.message}`);
    }
  },
  
  /**
   * 验证密钥长度是否符合要求
   * @param {string} key - 密钥字符串
   * @param {string} keyLength - 密钥长度（位）: '128', '192', '256'
   * @returns {boolean} - 验证结果
   */
  validateKeyLength: (key, keyLength) => {
    // AES-128需要16字节(128位)的密钥
    // AES-192需要24字节(192位)的密钥
    // AES-256需要32字节(256位)的密钥
    const requiredBytes = parseInt(keyLength) / 8;
    const keyBytes = new TextEncoder().encode(key).length;
    return keyBytes === requiredBytes;
  },
  
  /**
   * 验证IV长度是否为16字节(128位)
   * @param {string} iv - 初始向量字符串
   * @returns {boolean} - 验证结果
   */
  validateIVLength: (iv) => {
    // AES的IV必须是16字节(128位)
    const ivBytes = new TextEncoder().encode(iv).length;
    return ivBytes === 16;
  },

  /**
   * 检查指定的加密模式是否需要IV
   * @param {string} mode - 加密模式
   * @returns {boolean} - 是否需要IV
   */
  requiresIV: (mode) => {
    return AESUtil.MODES[mode]?.requiresIV ?? true;
  },

  /**
   * 获取加密模式的描述信息
   * @param {string} mode - 加密模式
   * @returns {string} - 描述信息
   */
  getModeDescription: (mode) => {
    return AESUtil.MODES[mode]?.description ?? '';
  },

  /**
   * 分析密钥长度处理情况
   * @param {string} key - 密钥字符串
   * @param {number} requestedLength - 请求的密钥长度（位）: 128, 192, 或 256
   * @returns {Object} - 分析结果
   */
  analyzeKeyHandling: (key, requestedLength) => {
    // 获取UTF-8编码的实际字节长度
    const actualBytes = new TextEncoder().encode(key).length;
    const actualBits = actualBytes * 8;
    
    // 检查CryptoJS如何解析密钥
    const parsedKey = CryptoJS.enc.Utf8.parse(key);
    
    // 分析CryptoJS对密钥的处理方式
    let cryptoJSHandling = '';
    const requiredBytes = requestedLength / 8;
    
    if (actualBytes < requiredBytes) {
      cryptoJSHandling = `CryptoJS会自动填充密钥至${requiredBytes}字节`;
    } else if (actualBytes > requiredBytes) {
      cryptoJSHandling = `CryptoJS会自动截断密钥至${requiredBytes}字节`;
    } else {
      cryptoJSHandling = '密钥长度符合要求，无需处理';
    }
    
    // 模拟CryptoJS密钥处理逻辑
    // CryptoJS在内部使用words数组存储密钥，每个word为4字节(32位)
    const wordsNeeded = Math.ceil(requiredBytes / 4);
    
    return {
      input: key,
      inputLength: key.length,
      actualBytes,
      actualBits,
      cryptoJSBytes: parsedKey.sigBytes,
      cryptoJSWords: parsedKey.words ? parsedKey.words.length : 0,
      neededWords: wordsNeeded,
      requestedBytes: requiredBytes,
      requestedBits: requestedLength,
      needsPadding: actualBytes < requiredBytes,
      exceedsRequest: actualBytes > requiredBytes,
      matchesRequest: actualBytes === requiredBytes,
      cryptoJSHandling,
      explanation: `CryptoJS在内部使用${wordsNeeded}个words存储AES-${requestedLength}的密钥，每个word为4字节`
    };
  },

  /**
   * 获取理想密钥长度
   * @param {number} keyLength - 密钥长度(位): 128, 192, 256
   * @returns {Object} - 密钥要求
   */
  getKeyRequirements: (keyLength) => {
    const bytes = keyLength / 8;
    return {
      bits: keyLength,
      bytes,
      words: Math.ceil(bytes / 4),
      description: `AES-${keyLength}需要${bytes}字节(${keyLength}位)的密钥`
    };
  }
};

export default AESUtil; 